import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SickIcon from '@mui/icons-material/Sick';

const StatBlock = ({ icon, label, value, sub, color, progress }) => (
    <Box sx={{
        flex: 1, minWidth: 140,
        p: 2, borderRadius: 2,
        border: '1px solid #EEF2F6',
        bgcolor: '#fff',
        display: 'flex', flexDirection: 'column', gap: 0.5,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ color, fontSize: 18, display: 'flex' }}>{icon}</Box>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1 }}>
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.25 }}>{sub}</Typography>
        )}
        {progress != null && (
            <LinearProgress
                variant="determinate"
                value={Math.min(100, progress)}
                sx={{
                    mt: 1, height: 4, borderRadius: 2,
                    bgcolor: '#EEF2F6',
                    '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 2 },
                }}
            />
        )}
    </Box>
);

export default function AbsenceBalanceCard({ summary }) {
    if (!summary) return null;

    const {
        vacationDaysTotal, vacationDaysTaken, vacationDaysPending,
        vacationDaysRemaining, vacationCarryOver, vacationDaysPerYear,
        sickDaysThisYear, timeBankHours, otherAbsences = {},
    } = summary;

    const vacProgress = vacationDaysTotal > 0
        ? (vacationDaysTaken / vacationDaysTotal) * 100 : 0;

    const bankLabel = timeBankHours >= 0
        ? `+${timeBankHours.toFixed(1)}h banked`
        : `${timeBankHours.toFixed(1)}h owed`;

    const bankColor = timeBankHours >= 0 ? '#0EA5E9' : '#F59E0B';

    const otherEntries = Object.entries(otherAbsences).filter(([, v]) => v > 0);

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Tooltip title={
                vacationCarryOver > 0
                    ? `${vacationDaysPerYear} days/year + ${vacationCarryOver} carried over`
                    : `${vacationDaysPerYear} days/year`
            }>
                <Box sx={{ flex: 1, minWidth: 140 }}>
                    <StatBlock
                        icon={<BeachAccessIcon fontSize="inherit" />}
                        label="Vacation"
                        color="#0D9488"
                        value={`${vacationDaysRemaining}d left`}
                        sub={`${vacationDaysTaken} taken · ${vacationDaysPending > 0 ? `${vacationDaysPending} pending · ` : ''}${vacationDaysTotal} total`}
                        progress={vacProgress}
                    />
                </Box>
            </Tooltip>

            <StatBlock
                icon={<AccessTimeIcon fontSize="inherit" />}
                label="Time Bank"
                color={bankColor}
                value={bankLabel}
                sub={timeBankHours >= 0
                    ? `≈ ${(timeBankHours / (summary.weeklyHours / 5 || 8)).toFixed(1)} comp days available`
                    : 'Hours owed'}
            />

            <StatBlock
                icon={<SickIcon fontSize="inherit" />}
                label="Sick Leave"
                color="#F59E0B"
                value={`${sickDaysThisYear}d`}
                sub="this year"
            />

            {otherEntries.map(([type, days]) => (
                <StatBlock
                    key={type}
                    icon={<BeachAccessIcon fontSize="inherit" />}
                    label={type.charAt(0) + type.slice(1).toLowerCase().replace('_', ' ')}
                    color="#94A3B8"
                    value={`${days}d`}
                    sub="this year"
                />
            ))}
        </Box>
    );
}
